import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import StateSelect from "@/components/StateSelect";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Upload, X, ImageIcon } from "lucide-react";
import { resolveAssetUrl } from "@/lib/assetUrl";

/**
 * BusinessEditDialog — reusable admin-only editor for a business record.
 *
 * Now supports: logo upload/removal, contact fields, marketing copy (bio,
 * founding_story), social media handles, and admin toggles (verified,
 * claimed, subscription tier). Backed by PATCH /api/admin/businesses/{id}.
 *
 * Props:
 *   business    — the business object (required to prefill)
 *   open        — controlled open state
 *   onOpenChange(open: boolean)
 *   onSaved(updatedBusiness) — called after a successful save
 */
export default function BusinessEditDialog({ business, open, onOpenChange, onSaved }) {
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    website: "",
    category: "",
    custom_category: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    description: "",
    bio: "",
    founding_story: "",
    logo_url: "",
    social_media: { instagram: "", facebook: "", twitter: "", linkedin: "", tiktok: "" },
    paypal_email: "",
    is_verified: false,
    is_claimed: false,
    subscription_tier: "free",
    subscription_status: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    // Fetch category list once for the dropdown
    axios
      .get(`${API}/categories`)
      .then((res) => setCategories(res.data?.categories || []))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (business) {
      setForm({
        name: business.name || "",
        email: business.email || "",
        phone: business.phone || "",
        website: business.website || "",
        category: business.category || "",
        custom_category: business.custom_category || "",
        address: business.address || "",
        city: business.city || "",
        state: business.state || "",
        zip_code: business.zip_code || "",
        description: business.description || "",
        bio: business.bio || "",
        founding_story: business.founding_story || "",
        logo_url: business.logo_url || "",
        social_media: {
          instagram: business.social_media?.instagram || "",
          facebook: business.social_media?.facebook || "",
          twitter: business.social_media?.twitter || "",
          linkedin: business.social_media?.linkedin || "",
          tiktok: business.social_media?.tiktok || "",
        },
        paypal_email: business.paypal_email || "",
        is_verified: !!business.is_verified,
        is_claimed: !!business.is_claimed,
        subscription_tier: business.subscription_tier || "free",
        subscription_status: business.subscription_status || "",
      });
    }
  }, [business]);

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo must be under 5MB");
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("image_type", "business");
      const r = await axios.post(`${API}/upload/image`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r.data?.url;
      if (!url) throw new Error("no url returned");
      // Backend now returns `/api/uploads/...` directly (correctly routed
      // through Kubernetes ingress). Store what the backend returns as-is.
      setForm((f) => ({ ...f, logo_url: url }));
      toast.success("Logo uploaded — click Save to keep it.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Logo upload failed");
    } finally {
      setUploadingLogo(false);
    }
  };

  const save = async () => {
    if (!business) return;
    setSaving(true);
    try {
      // Normalise: only send custom_category when category === "Other"
      const payload = { ...form };
      if (payload.category !== "Other") payload.custom_category = "";
      const r = await axios.patch(
        `${API}/admin/businesses/${business.business_id}`,
        payload,
        { withCredentials: true }
      );
      toast.success("Saved.");
      onSaved?.(r.data?.business);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setSocial = (key, val) =>
    setForm((f) => ({ ...f, social_media: { ...f.social_media, [key]: val } }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-tbc-navy">
            Edit — {business?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* -------------------- LOGO -------------------- */}
          <section>
            <Label className="font-body text-tbc-navy">Logo</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-200 bg-tbc-cream/40 flex items-center justify-center overflow-hidden flex-shrink-0">
                {form.logo_url ? (
                  <img
                    src={resolveAssetUrl(form.logo_url)}
                    alt="logo"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <ImageIcon className="w-6 h-6 text-gray-400" />
                )}
              </div>
              <div className="flex-1 flex flex-wrap gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => uploadLogo(e.target.files?.[0])}
                  data-testid="biz-edit-logo-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={uploadingLogo}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="biz-edit-logo-upload"
                >
                  {uploadingLogo ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Upload className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  {form.logo_url ? "Replace logo" : "Upload logo"}
                </Button>
                {form.logo_url && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    onClick={() => setForm({ ...form, logo_url: "" })}
                    data-testid="biz-edit-logo-remove"
                  >
                    <X className="w-3.5 h-3.5 mr-1.5" /> Remove
                  </Button>
                )}
              </div>
            </div>
            <Input
              className="mt-2"
              value={form.logo_url}
              onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
              placeholder="Or paste a URL — /api/uploads/... or https://..."
              data-testid="biz-edit-logo-url"
            />
          </section>

          {/* -------------------- CONTACT -------------------- */}
          <section className="space-y-3">
            <div>
              <Label className="font-body text-tbc-navy">Business name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="biz-edit-name"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="font-body text-tbc-navy">Contact email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="biz-edit-email"
                />
              </div>
              <div>
                <Label className="font-body text-tbc-navy">Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="font-body text-tbc-navy">Category</Label>
                <Select
                  value={form.category || undefined}
                  onValueChange={(v) => setForm({
                    ...form,
                    category: v,
                    // Clear write-in when switching away from Other
                    custom_category: v === "Other" ? form.custom_category : "",
                  })}
                >
                  <SelectTrigger data-testid="business-edit-category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {(categories.length ? categories : ["Food & Beverage", "Other"]).map((c) => (
                      <SelectItem key={c} value={c} data-testid={`business-edit-category-option-${c.replace(/\s+/g, "-").toLowerCase()}`}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-body text-tbc-navy">Website</Label>
                <Input
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                />
              </div>
            </div>
            {form.category === "Other" && (
              <div>
                <Label className="font-body text-tbc-navy">Specify your field *</Label>
                <Input
                  value={form.custom_category}
                  onChange={(e) => setForm({ ...form, custom_category: e.target.value })}
                  placeholder="e.g. DJ services, tax prep, event catering"
                  data-testid="business-edit-custom-category"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  This shows up on the profile in place of &ldquo;Other&rdquo;.
                </p>
              </div>
            )}
            <div>
              <Label className="font-body text-tbc-navy">Address</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 sm:col-span-1">
                <Label className="font-body text-tbc-navy">City</Label>
                <Input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </div>
              <div>
                <Label className="font-body text-tbc-navy">State</Label>
                <StateSelect
                  value={form.state}
                  onChange={(code) => setForm({ ...form, state: code })}
                  testId="business-edit-state-select"
                />
              </div>
              <div>
                <Label className="font-body text-tbc-navy">ZIP</Label>
                <Input
                  value={form.zip_code}
                  onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                />
              </div>
            </div>
          </section>

          {/* -------------------- COPY -------------------- */}
          <section className="space-y-3">
            <div>
              <Label className="font-body text-tbc-navy">Short description</Label>
              <Textarea
                rows={4}
                className="min-h-[110px] leading-relaxed"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1 font-body">
                {(form.description || "").length} characters
              </p>
            </div>
            <div>
              <Label className="font-body text-tbc-navy">Bio</Label>
              <Textarea
                rows={4}
                className="min-h-[110px] leading-relaxed"
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
              />
            </div>
            <div>
              <Label className="font-body text-tbc-navy">Founding story</Label>
              <Textarea
                rows={4}
                className="min-h-[110px] leading-relaxed"
                value={form.founding_story}
                onChange={(e) => setForm({ ...form, founding_story: e.target.value })}
              />
            </div>
          </section>

          {/* -------------------- SOCIAL -------------------- */}
          <section>
            <Label className="font-body text-tbc-navy">Social media handles</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              {[
                ["instagram", "Instagram"],
                ["facebook", "Facebook"],
                ["twitter", "Twitter / X"],
                ["linkedin", "LinkedIn"],
                ["tiktok", "TikTok"],
              ].map(([key, label]) => (
                <div key={key}>
                  <label className="text-xs text-gray-500 font-body">{label}</label>
                  <Input
                    value={form.social_media[key]}
                    onChange={(e) => setSocial(key, e.target.value)}
                    placeholder="@handle or full URL"
                  />
                </div>
              ))}
            </div>
          </section>

          {/* -------------------- ADMIN CONTROLS -------------------- */}
          <section className="rounded-lg border border-tbc-gold/30 bg-tbc-gold/[0.04] p-4 space-y-4">
            <p className="text-xs uppercase tracking-widest text-tbc-gold font-body">
              Admin controls
            </p>
            <div>
              <label className="text-xs text-gray-500 font-body">
                Seller PayPal email (used for admin payouts)
              </label>
              <Input
                type="email"
                value={form.paypal_email}
                onChange={(e) => setForm({ ...form, paypal_email: e.target.value })}
                placeholder="seller@example.com"
                data-testid="biz-edit-paypal-email"
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="font-body text-tbc-navy text-sm">Verified (gold badge)</label>
              <Switch
                checked={form.is_verified}
                onCheckedChange={(v) => setForm({ ...form, is_verified: !!v })}
                data-testid="biz-edit-toggle-verified"
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="font-body text-tbc-navy text-sm">Claimed (owner-managed)</label>
              <Switch
                checked={form.is_claimed}
                onCheckedChange={(v) => setForm({ ...form, is_claimed: !!v })}
                data-testid="biz-edit-toggle-claimed"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 font-body">Subscription tier</label>
                <select
                  value={form.subscription_tier}
                  onChange={(e) => setForm({ ...form, subscription_tier: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-body"
                  data-testid="biz-edit-tier"
                >
                  <option value="free">Free</option>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                  <option value="founding">Founding</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 font-body">Subscription status</label>
                <select
                  value={form.subscription_status || ""}
                  onChange={(e) => setForm({ ...form, subscription_status: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-body"
                  data-testid="biz-edit-sub-status"
                >
                  <option value="">— none —</option>
                  <option value="active">active</option>
                  <option value="canceled">canceled</option>
                  <option value="past_due">past_due</option>
                  <option value="trialing">trialing</option>
                </select>
              </div>
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-tbc-navy text-white hover:bg-tbc-navy/90"
            data-testid="biz-edit-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
